import { fmtCurrency } from '../../../utils/formatNumber';
import styles from './OpeningBalance.module.css';

const SubGroupTableSection = ({ subGroups, emptyLabel }) => {
  if (!subGroups?.length) {
    return (
      <tbody>
        <tr>
          <td colSpan={2} className={styles.emptyCell}>{emptyLabel}</td>
        </tr>
      </tbody>
    );
  }

  return (
    <>
      {subGroups.map((subGroup) => (
        <tbody key={subGroup.name}>
          <tr className={styles.sectionHeaderRow}>
            <td colSpan={2}>{subGroup.name}</td>
          </tr>
          {subGroup.rows.map((row) => (
            <tr key={row.id}>
              <td>
                {row.name}
                {row.accountCode && <span className={styles.metaText}>{row.accountCode}</span>}
              </td>
              <td className={styles.amtRight}>{fmtCurrency(row.balance)}</td>
            </tr>
          ))}
          <tr className={styles.subtotalRow}>
            <td>Subtotal - {subGroup.name}</td>
            <td className={styles.amtRight}>{fmtCurrency(subGroup.subtotal)}</td>
          </tr>
        </tbody>
      ))}
    </>
  );
};

const FlatRowsSection = ({ rows, emptyLabel, renderLabel }) => (
  <tbody>
    {rows?.length ? rows.map((row) => (
      <tr key={row.id}>
        <td>{renderLabel(row)}</td>
        <td className={styles.amtRight}>{fmtCurrency(row.balance)}</td>
      </tr>
    )) : (
      <tr>
        <td colSpan={2} className={styles.emptyCell}>{emptyLabel}</td>
      </tr>
    )}
  </tbody>
);

export default function OpeningBalanceReport({ data }) {
  if (!data) return null;

  const {
    assets,
    liabilities,
    equity,
    incomeExpense,
    totalLiabilitiesEquity,
    isBalanced,
  } = data;
  const surplus = incomeExpense?.surplus || 0;
  const difference = Math.abs((assets?.total || 0) - (totalLiabilitiesEquity || 0));

  return (
    <div className={`${styles.reportStack} report-page`}>
      <div className={styles.reportGrid}>
        <div className={styles.reportRightCol}>
          <div className="card">
            <div className="card-header">
              <h3>Liabilities</h3>
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Account</th>
                    <th className={styles.amtRight}>Amount (Rs)</th>
                  </tr>
                </thead>
                <SubGroupTableSection subGroups={liabilities?.subGroups} emptyLabel="No liability opening balances" />
                <tfoot>
                  <tr>
                    <td><strong>Total Liabilities</strong></td>
                    <td className={styles.amtRight}><strong>{fmtCurrency(liabilities?.total || 0)}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3>Equity &amp; Corpus</h3>
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Account</th>
                    <th className={styles.amtRight}>Amount (Rs)</th>
                  </tr>
                </thead>
                <FlatRowsSection
                  rows={equity?.rows}
                  emptyLabel="No equity opening balances"
                  renderLabel={(row) => (
                    <>
                      {row.name}
                      {row.accountCode && <span className={styles.metaText}>{row.accountCode}</span>}
                    </>
                  )}
                />
                <tbody>
                  <tr>
                    <td><em>Net Income B/F</em></td>
                    <td className={styles.amtRight}>{fmtCurrency(surplus)}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td><strong>Total Equity</strong></td>
                    <td className={styles.amtRight}><strong>{fmtCurrency((equity?.total || 0) + surplus)}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Assets</h3>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Account</th>
                  <th className={styles.amtRight}>Amount (Rs)</th>
                </tr>
              </thead>
              <SubGroupTableSection subGroups={assets?.subGroups} emptyLabel="No asset opening balances" />
              <tfoot>
                <tr>
                  <td><strong>Total Assets</strong></td>
                  <td className={styles.amtRight}><strong>{fmtCurrency(assets?.total || 0)}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      <div className="card">
        <div className={styles.reportTotals}>
          <div className={styles.reportTotalCell}>
            <span className={styles.reportTotalLabel}>Total Liabilities + Equity</span>
            <strong>{fmtCurrency(totalLiabilitiesEquity || 0)}</strong>
          </div>
          <div className={styles.reportTotalCell}>
            <span className={styles.reportTotalLabel}>Total Assets</span>
            <strong>{fmtCurrency(assets?.total || 0)}</strong>
          </div>
        </div>
        <div className={`${styles.reportBalanceBar} ${isBalanced ? styles.balanceOk : styles.balanceDiff}`}>
          {isBalanced ? 'Liabilities + Equity = Assets' : `Difference: ${fmtCurrency(Math.abs(difference || 0))}`}
        </div>
      </div>
    </div>
  );
}
